import { useRouter } from '@tanstack/react-router';
import { PropsWithChildren, useEffect } from 'react';

import { useTabbar } from '@common/providers/useTabbar';
import { useAppStore } from '@common/stores/useAppStore';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Menu } from '@common/ui/Menu';
import { useEventEmit } from '@common/utils/events';

import {
  useDeleteMatchById,
  usePutBlockMatchById,
} from '@services/hooks/MatchesService';
import { type Match } from '@services/MatchesService';

import { useChatSafetyContext } from './useChatSafetyContext';

export type ChatSafetyHocProps = PropsWithChildren & {
  profile?: Match;
  matchId: string;
  scrollToBottom: (behavior?: ScrollBehavior, force?: boolean) => void;
};

export const ChatSafetyHoc: React.FC<ChatSafetyHocProps> = ({
  children,
  profile,
  matchId,
  scrollToBottom,
}) => {
  const router = useRouter();
  const tabbar = useTabbar();
  const emit = useEventEmit();
  const { optimisticallyDeleteChat, optimisticallyUpdateMatchBlocked } =
    useAppStore();

  const {
    chatSafetyState,
    updateChatSafetyState,
    chatVisibleBySafetySelector,
  } = useChatSafetyContext();
  const currentMatchId = profile?.match_id;

  const { trigger: triggerPutBlockMatchById } = usePutBlockMatchById();
  const { trigger: triggerDeleteMatchById } = useDeleteMatchById();

  const blockMatch = async ({
    matchId: currentMatchId,
    blocked,
  }: {
    matchId: string;
    blocked: boolean;
  }) => {
    try {
      updateChatSafetyState('isDialog', false);
      updateChatSafetyState('isConfirmBlocking', false);
      optimisticallyUpdateMatchBlocked(currentMatchId, blocked);
      await triggerPutBlockMatchById({
        matchId: currentMatchId,
        blocked,
      });

      emit('success', {
        title: `Пользователь успешно ${blocked ? 'заблокирован' : 'разблокирован'}`,
      });
    } catch (error) {
      emit('error', {
        title: `Не удалось ${blocked ? 'заблокировать' : 'разблокировать'} пользователя`,
        description: 'Попробуйте еще раз',
      });
      optimisticallyUpdateMatchBlocked(currentMatchId, !blocked);
    }
  };

  const deleteChat = async ({
    matchId: currentMatchId,
  }: {
    matchId: string;
  }) => {
    try {
      optimisticallyDeleteChat(currentMatchId);
      router.history.push('/chats');
      updateChatSafetyState('isDialog', false);
      updateChatSafetyState('isDeleting', false);
      await triggerDeleteMatchById({ matchId: currentMatchId });

      emit('success', {
        title: 'Диалог успешно удален',
      });
    } catch (error) {
      emit('error', {
        title: 'Не удалось удалить диалог',
        description: 'Попробуйте еще раз',
      });
      optimisticallyDeleteChat(currentMatchId, true);
    }
  };

  const isVisibleChat = chatVisibleBySafetySelector(profile);

  useEffect(() => {
    if (isVisibleChat) {
      // при открытии/закрытии BottomSheet чат чуть скроллится вверх; возвращаем его на место
      scrollToBottom('auto');
    }
  }, [isVisibleChat]);

  useEffect(() => {
    if (chatSafetyState.isDialog) {
      tabbar.setBottomSheet(
        <BottomSheet
          buttons={[
            <Button
              testId="chat-safety-close-button"
              key="close"
              onClick={() => updateChatSafetyState('isDialog', false)}
            >
              Закрыть
            </Button>,
          ]}
        >
          <Menu
            items={[
              {
                label: 'Заблокировать',
                onClick: () => {
                  updateChatSafetyState('isDialog', false);
                  updateChatSafetyState('isConfirmBlocking', true);
                },
              },
              {
                label: 'Пожаловаться',
                onClick: () => {
                  updateChatSafetyState('isDialog', false);
                  router.history.push(
                    `/dating/report?profileId=${profile?.id ?? ''}&type=chat&matchId=${matchId}`,
                  );
                },
              },
              {
                label: 'Удалить чат',
                onClick: () => {
                  updateChatSafetyState('isDialog', false);
                  updateChatSafetyState('isDeleting', true);
                },
              },
            ]}
          />
        </BottomSheet>,
        { isAbsolute: true },
      );
      return;
    }

    if (chatSafetyState.isConfirmBlocking) {
      tabbar.setBottomSheet(
        <BottomSheet
          title="Заблокировать"
          subtitle="Пользователь больше не сможет написать вам и не увидит в свайпах"
          buttons={[
            <Button
              testId="chat-safety-block-button"
              key="block"
              onClick={() =>
                blockMatch({ blocked: true, matchId: currentMatchId ?? '' })
              }
            >
              Заблокировать
            </Button>,
            <Button
              testId="chat-safety-block-cancel-button"
              key="cancel"
              variant="actionSecondary"
              onClick={() => {
                updateChatSafetyState('isConfirmBlocking', false);
                updateChatSafetyState('isDialog', true);
              }}
            >
              Отмена
            </Button>,
          ]}
        />,
      );
      return;
    }

    if (chatSafetyState.isDeleting) {
      tabbar.setBottomSheet(
        <BottomSheet
          title="Удалить чат"
          subtitle="История чата удалится навсегда для вас обоих"
          buttons={[
            <Button
              testId="chat-safety-delete-button"
              onClick={() => deleteChat({ matchId: currentMatchId ?? '' })}
              key="delete"
            >
              Удалить
            </Button>,
            <Button
              testId="chat-safety-delete-cancel-button"
              key="cancel"
              variant="actionSecondary"
              onClick={() => {
                updateChatSafetyState('isDeleting', false);
                updateChatSafetyState('isDialog', true);
              }}
            >
              Отмена
            </Button>,
          ]}
        />,
      );
      return;
    }

    if (profile?.blocked_by_me) {
      tabbar.setBottomSheet(
        <BottomSheet
          buttons={[
            <Button
              testId="chat-safety-unblock-button"
              key="unblock"
              variant="actionSecondary"
              onClick={() =>
                blockMatch({ blocked: false, matchId: currentMatchId ?? '' })
              }
            >
              Разблокировать
            </Button>,
          ]}
        />,
      );
      return;
    }

    if (profile?.blocked_me) {
      tabbar.setBottomSheet(
        <BottomSheet subtitle="Вы заблокированы этим пользователем" />,
      );
      return;
    }

    tabbar.setBottomSheet(null);
  }, [chatSafetyState, profile]);

  if (profile?.blocked_by_me || profile?.blocked_me || !isVisibleChat) {
    return null;
  }

  return children;
};
